import React, {useEffect, useMemo, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {Box, Paper, Typography, useTheme} from '@mui/material';
import {alpha} from '@mui/material/styles';
import {Dayjs} from 'dayjs';

import {CalendarItem, groupByDay, hourRange, layoutDay, timeLabel, weekDays} from './calendarModel';

/** Höhe einer Stunde in Pixeln. 56 trägt zwei Zeilen Text in einem 30-Minuten-Termin. */
const HOUR_HEIGHT = 56;

/** Breite der Zeitachse links. */
const GUTTER = 56;

interface Props {
    anchor: Dayjs;
    items: CalendarItem[];
    /** Aktuelle Uhrzeit; bestimmt die Jetzt-Linie. */
    now: Dayjs;
    onSelect: (item: CalendarItem) => void;
}

/**
 * Wochenansicht als Zeitraster: Tage als Spalten, Stunden als Zeilen — die Darstellung, die man
 * aus Outlook und Kalendern kennt.
 *
 * Ganztägige Elemente (Urlaub, Krank, Wiedervorlagen ohne Uhrzeit) stehen in einem eigenen Band
 * über dem Raster. Sie im Raster zu zeichnen hieße, ihnen eine Uhrzeit anzudichten, die sie in
 * Labelwin nicht haben.
 */
export const WeekGrid: React.FC<Props> = ({anchor, items, now, onSelect}) => {
    const {t, i18n} = useTranslation();
    const theme = useTheme();
    const scrollRef = useRef<HTMLDivElement>(null);

    const days = useMemo(() => weekDays(anchor), [anchor]);
    const byDay = useMemo(() => groupByDay(items), [items]);
    const {from, to} = useMemo(() => hourRange(items), [items]);
    const hours = useMemo(
        () => Array.from({length: to - from}, (_, i) => from + i),
        [from, to]);

    const gridHeight = (to - from) * HOUR_HEIGHT;
    const todayKey = now.format('YYYY-MM-DD');
    const nowMin = now.hour() * 60 + now.minute();
    const nowVisible = nowMin >= from * 60 && nowMin <= to * 60;
    const nowTop = ((nowMin - from * 60) / 60) * HOUR_HEIGHT;

    // Beim Öffnen zur aktuellen Zeit scrollen statt an den Anfang des Rasters — sonst sieht man
    // zuerst den frühen Morgen, an dem selten etwas steht.
    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;
        const target = nowVisible ? nowTop - container.clientHeight / 3 : 0;
        container.scrollTop = Math.max(0, target);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anchor.valueOf(), from, to]);

    const weekdayLabel = (day: Dayjs) =>
        new Intl.DateTimeFormat(i18n.language, {weekday: 'short'}).format(day.toDate());

    const allDayRows = Math.max(
        ...days.map((day) => (byDay.get(day.format('YYYY-MM-DD')) ?? []).filter((i) => i.allDay).length),
        0);

    const columns = `${GUTTER}px repeat(7, minmax(0, 1fr))`;

    return (
        <Paper variant="outlined" sx={{overflow: 'hidden'}}>
            {/* Kopfzeile: Wochentag und Datum, heute hervorgehoben. */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: columns,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
            }}>
                <Box/>
                {days.map((day) => {
                    const isToday = day.format('YYYY-MM-DD') === todayKey;
                    const isWeekend = day.day() === 0 || day.day() === 6;
                    return (
                        <Box key={day.valueOf()} sx={{
                            py: 0.75,
                            textAlign: 'center',
                            borderLeft: 1,
                            borderColor: 'divider',
                            bgcolor: isWeekend ? 'action.hover' : undefined,
                        }}>
                            <Typography variant="caption" color={isToday ? 'primary' : 'text.secondary'}
                                        sx={{display: 'block', textTransform: 'uppercase'}}>
                                {weekdayLabel(day)}
                            </Typography>
                            <Typography variant="subtitle2"
                                        sx={{
                                            fontWeight: isToday ? 700 : 500,
                                            color: isToday ? 'primary.contrastText' : 'text.primary',
                                            bgcolor: isToday ? 'primary.main' : 'transparent',
                                            borderRadius: '50%',
                                            width: 26,
                                            height: 26,
                                            lineHeight: '26px',
                                            mx: 'auto',
                                        }}>
                                {day.date()}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>

            {/* Ganztagsband — nur sichtbar, wenn es etwas zu zeigen gibt. */}
            {allDayRows > 0 && (
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: columns,
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: alpha(theme.palette.action.hover, 0.4),
                }}>
                    <Box sx={{px: 0.5, py: 0.5, textAlign: 'right'}}>
                        <Typography variant="caption" color="text.secondary">
                            {t('calendar.allDayShort')}
                        </Typography>
                    </Box>
                    {days.map((day) => {
                        const allDay = (byDay.get(day.format('YYYY-MM-DD')) ?? []).filter((i) => i.allDay);
                        return (
                            <Box key={day.valueOf()}
                                 sx={{borderLeft: 1, borderColor: 'divider', p: 0.5, minHeight: 28}}>
                                {allDay.map((item) => (
                                    <Box key={item.key} onClick={() => onSelect(item)} sx={{
                                        cursor: 'pointer',
                                        px: 0.75,
                                        py: 0.25,
                                        mb: 0.25,
                                        borderRadius: 0.5,
                                        borderLeft: 3,
                                        borderColor: `${item.color}.main`,
                                        bgcolor: alpha(theme.palette[item.color].main, 0.14),
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}>
                                        <Typography variant="caption" sx={{fontWeight: 500}}>
                                            {item.title}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        );
                    })}
                </Box>
            )}

            {/* Zeitraster. */}
            <Box ref={scrollRef} sx={{overflowY: 'auto', maxHeight: {xs: '60vh', md: 'calc(100vh - 340px)'}}}>
                <Box sx={{display: 'grid', gridTemplateColumns: columns, position: 'relative'}}>
                    {/* Zeitachse. */}
                    <Box sx={{position: 'relative', height: gridHeight}}>
                        {hours.map((hour, index) => (
                            <Typography key={hour} variant="caption" color="text.secondary" sx={{
                                position: 'absolute',
                                top: index * HOUR_HEIGHT - 8,
                                right: 6,
                            }}>
                                {String(hour).padStart(2, '0')}:00
                            </Typography>
                        ))}
                    </Box>

                    {days.map((day) => {
                        const key = day.format('YYYY-MM-DD');
                        const dayItems = byDay.get(key) ?? [];
                        const placed = layoutDay(dayItems);
                        const isToday = key === todayKey;
                        const isWeekend = day.day() === 0 || day.day() === 6;

                        return (
                            <Box key={day.valueOf()} sx={{
                                position: 'relative',
                                height: gridHeight,
                                borderLeft: 1,
                                borderColor: 'divider',
                                bgcolor: isWeekend ? 'action.hover' : undefined,
                            }}>
                                {/* Stundenlinien. */}
                                {hours.map((hour, index) => (
                                    <Box key={hour} sx={{
                                        position: 'absolute',
                                        top: index * HOUR_HEIGHT,
                                        left: 0,
                                        right: 0,
                                        borderTop: 1,
                                        borderColor: 'divider',
                                    }}/>
                                ))}

                                {placed.map(({item, lane, lanes}) => {
                                    const top = ((item.startMin - from * 60) / 60) * HOUR_HEIGHT;
                                    const height = Math.max(18, ((item.endMin - item.startMin) / 60) * HOUR_HEIGHT - 2);
                                    const width = 100 / lanes;

                                    return (
                                        <Box key={item.key} onClick={() => onSelect(item)} sx={{
                                            position: 'absolute',
                                            top,
                                            height,
                                            left: `calc(${lane * width}% + 2px)`,
                                            width: `calc(${width}% - 4px)`,
                                            cursor: 'pointer',
                                            overflow: 'hidden',
                                            borderRadius: 0.5,
                                            borderLeft: 3,
                                            borderColor: `${item.color}.main`,
                                            bgcolor: alpha(theme.palette[item.color].main, 0.16),
                                            px: 0.75,
                                            py: 0.25,
                                            '&:hover': {bgcolor: alpha(theme.palette[item.color].main, 0.28)},
                                        }}>
                                            <Typography variant="caption" sx={{
                                                display: 'block',
                                                fontWeight: 600,
                                                lineHeight: 1.25,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}>
                                                {item.title}
                                            </Typography>
                                            {height > 34 && (
                                                <Typography variant="caption" color="text.secondary" sx={{
                                                    display: 'block',
                                                    lineHeight: 1.2,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                    {timeLabel(item, t('calendar.allDay'))}
                                                    {item.subtitle ? ` · ${item.subtitle}` : ''}
                                                </Typography>
                                            )}
                                        </Box>
                                    );
                                })}

                                {/* Jetzt-Linie, nur in der Spalte des heutigen Tages. */}
                                {isToday && nowVisible && (
                                    <Box sx={{
                                        position: 'absolute',
                                        top: nowTop,
                                        left: 0,
                                        right: 0,
                                        height: 0,
                                        borderTop: 2,
                                        borderColor: 'error.main',
                                        zIndex: 2,
                                        pointerEvents: 'none',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            left: -5,
                                            top: -5,
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            bgcolor: 'error.main',
                                        },
                                    }}/>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Paper>
    );
};

export default WeekGrid;
